<template>
  <b-card
    no-body
    class="modern-card mb-3"
    :class="{ 'row-card': isRowMode }"
  >
    <b-card-body class="modern-card-body">
      <div
        v-if="content.is_deleted"
        class="tombstone"
      >
        <i class="fa fa-trash mr-2" />
        Esta publicación fue eliminada por <strong>{{ content.deleted_by_name }}</strong>
      </div>

      <template v-else>
        <div :class="['card-header-row', isRowMode ? 'mb-1' : 'mb-3']">
          <div class="post-meta">
            <b-avatar
              variant="primary"
              :text="getAvatarText"
              class="mr-3"
            />
            <div>
              <div class="post-author">
                {{ content.name }}
              </div>
              <div class="post-store">
                {{ content.retail_name_store_name }}
              </div>
              <div class="post-date">
                {{ formattedDate }}
              </div>
            </div>
          </div>
          <feed-delete-button
            v-if="canDelete"
            :deleting="deleting"
            @delete="handleDelete"
          />
        </div>

        <div :class="['card-body-content', isRowMode ? 'body-row' : 'body-list']">
          <div class="text-side">
            <p
              v-if="captionSegments.length"
              class="post-caption"
            >
              <template v-for="(seg, i) in captionSegments">
                <span
                  v-if="seg.type === 'text'"
                  :key="'t' + i"
                >{{ seg.value }}</span>
                <span
                  v-else-if="seg.type === 'hashtag'"
                  :key="'h' + i"
                  class="caption-ht"
                  :style="{
                    background: seg.hashtag.color + '22',
                    color: seg.hashtag.color,
                    borderColor: seg.hashtag.color + '55'
                  }"
                >{{ seg.raw }}</span>
                <span
                  v-else
                  :key="'m' + i"
                  class="caption-mention"
                >@{{ seg.name }}</span>
              </template>
            </p>

            <div
              v-if="content.retail_product || content.vendor_product"
              class="product-info"
            >
              <div
                v-if="content.retail_product"
                class="product-tag"
              >
                <i class="fa fa-tag mr-1" />
                <span class="product-label">Retail:</span> {{ content.retail_product }}
              </div>
              <div
                v-if="content.vendor_product"
                class="product-tag"
              >
                <i class="fa fa-tag mr-1" />
                <span class="product-label">Proveedor:</span> {{ content.vendor_product }}
              </div>
            </div>

            <post-hashtags
              :content="content"
              @hashtag-added="$emit('hashtag-added')"
            />

            <div class="comments-area">
              <div
                v-if="content.latest_comment_content && !showComments && !isRowMode"
                class="latest-comment-preview"
              >
                <b-avatar
                  size="18"
                  variant="secondary"
                  :text="latestCommentInitials"
                  class="mr-1 flex-shrink-0"
                />
                <span class="preview-text">
                  <strong>{{ content.latest_comment_author }}</strong>
                  {{ content.latest_comment_content }}
                </span>
              </div>

              <button
                class="toggle-comments-btn"
                @click="toggleComments"
              >
                <i
                  class="mr-1"
                  :class="showComments ? 'fa fa-chevron-up' : 'fa fa-comment-o'"
                />
                {{ showComments ? 'Ocultar comentarios' : (content.latest_comment_content ? 'Ver comentarios' : 'Comentar') }}
              </button>

              <post-comments
                v-if="showComments"
                :post-id="content.id"
              />
            </div>
          </div>

          <viewer
            v-if="hasImages || !isRowMode"
            ref="viewer"
            :images="imgList"
            class="viewer media-side"
          >
            <template #default="scope">
              <div class="carousel-wrap">
                <img
                  v-for="(src, i) in scope.images"
                  :key="src"
                  :src="src"
                  :class="['post-image', { 'car-hidden': i !== carouselIndex }]"
                  alt="Imagen publicación"
                >
                <template v-if="scope.images.length > 1">
                  <button
                    class="car-btn car-prev"
                    @click.stop="prevImage(scope.images.length)"
                  >
                    <i class="fa fa-chevron-left" />
                  </button>
                  <button
                    class="car-btn car-next"
                    @click.stop="nextImage(scope.images.length)"
                  >
                    <i class="fa fa-chevron-right" />
                  </button>
                  <span class="car-counter">{{ carouselIndex + 1 }}/{{ scope.images.length }}</span>
                </template>
              </div>
            </template>
          </viewer>
        </div>
      </template>
    </b-card-body>
  </b-card>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import { deletePost } from '../Services'
import FeedDeleteButton from './FeedDeleteButton.vue'
import PostComments from './PostComments.vue'
import PostHashtags from './PostHashtags.vue'

export default {
  name: 'NewsCard',
  components: {
    FeedDeleteButton,
    PostComments,
    PostHashtags
  },
  props: {
    content: {
      type: Object,
      required: true
    },
    viewMode: {
      type: String,
      default: 'list'
    }
  },
  setup (props, context) {
    const deleting = ref(false)
    const showComments = ref(false)

    const isRowMode = computed(() => props.viewMode === 'row')

    const captionSegments = computed(() => {
      const caption = props.content.content?.caption
      if (!caption) return []

      const hashtagMap = Object.fromEntries(
        (props.content.hashtags || []).map(h => [h.name.toLowerCase(), h])
      )
      const mentionMap = Object.fromEntries(
        (props.content.mentions || []).map(m => [m.name.trim().split(' ')[0].toLowerCase(), m])
      )

      const segments = []
      const regex = /(#\w+|@\w+)/g
      let lastIndex = 0
      let match

      while ((match = regex.exec(caption)) !== null) {
        if (match.index > lastIndex) {
          segments.push({ type: 'text', value: caption.slice(lastIndex, match.index) })
        }
        const token = match[0]
        const word = token.slice(1).toLowerCase()

        if (token.startsWith('#')) {
          const ht = hashtagMap[word]
          segments.push(ht
            ? { type: 'hashtag', hashtag: ht, raw: token }
            : { type: 'text', value: token }
          )
        } else {
          const mention = mentionMap[word]
          segments.push(mention
            ? { type: 'mention', name: mention.name.trim() }
            : { type: 'text', value: token }
          )
        }
        lastIndex = match.index + token.length
      }

      if (lastIndex < caption.length) {
        segments.push({ type: 'text', value: caption.slice(lastIndex) })
      }
      return segments
    })

    const imgList = computed(() => props.content.content?.img_list || [])

    const hasImages = computed(() => imgList.value.length > 0)

    const carouselIndex = ref(0)

    const prevImage = (total) => {
      carouselIndex.value = (carouselIndex.value - 1 + total) % total
    }

    const nextImage = (total) => {
      carouselIndex.value = (carouselIndex.value + 1) % total
    }

    const canDelete = computed(() => !props.content.is_deleted)

    const getAvatarText = computed(() =>
      props.content.name.split(' ').map(n => n[0]).join('')
    )

    const latestCommentInitials = computed(() => {
      const name = props.content.latest_comment_author || ''
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
    })

    const formattedDate = computed(() =>
      context.root.$moment(props.content.created_at).format('D MMMM YYYY, h:mm:ss a')
    )

    const toggleComments = () => {
      showComments.value = !showComments.value
    }

    const handleDelete = async () => {
      deleting.value = true
      try {
        await deletePost(props.content.id)
        context.emit('deleted', props.content.id)
      } catch {
        // silent
      } finally {
        deleting.value = false
      }
    }

    return {
      deleting,
      showComments,
      isRowMode,
      captionSegments,
      hasImages,
      imgList,
      carouselIndex,
      prevImage,
      nextImage,
      canDelete,
      getAvatarText,
      latestCommentInitials,
      formattedDate,
      toggleComments,
      handleDelete
    }
  }
}
</script>

<style scoped>
.modern-card {
  border: none;
  border-radius: 10px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.07);
  overflow: hidden;
}

.modern-card-body {
  padding: 1rem;
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.post-meta {
  display: flex;
  align-items: center;
}

.post-author {
  font-size: 0.8rem;
  font-weight: 600;
  color: #2d3748;
}

.post-store {
  font-size: 0.75rem;
  color: #718096;
}

.post-date {
  font-size: 0.7rem;
  color: #a0aec0;
}

/* Carousel */
.carousel-wrap {
  position: relative;
  width: 100%;
  margin-top: 0.75rem;
}

.body-row .carousel-wrap {
  margin-top: 0;
}

.post-image {
  width: 100%;
  height: 220px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  display: block;
  transition: transform 0.2s ease;
}

.post-image:hover {
  transform: scale(1.02);
}

.car-hidden {
  display: none;
}

.car-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 0, 0, 0.45);
  border: none;
  color: white;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.65rem;
  z-index: 1;
  transition: background 0.15s;
}

.car-btn:hover {
  background: rgba(0, 0, 0, 0.65);
}

.car-prev { left: 0.35rem; }
.car-next { right: 0.35rem; }

.car-counter {
  position: absolute;
  bottom: 0.35rem;
  right: 0.35rem;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 0.62rem;
  padding: 0.1rem 0.35rem;
  border-radius: 10px;
  line-height: 1.4;
  pointer-events: none;
}

/* Layout modes */
.card-body-content {
  display: flex;
}

.body-list {
  flex-direction: column;
}

.body-list .media-side {
  order: -1;
  margin-bottom: 0.75rem;
}

.body-row {
  flex-direction: row;
  gap: 0.75rem;
  align-items: flex-start;
}

.body-row .text-side {
  flex: 1;
  min-width: 0;
}

.body-row .media-side {
  flex-shrink: 0;
  width: 120px;
}

.body-row .post-image {
  height: 88px;
  border-radius: 5px;
}

/* Row card compact overrides */
.row-card .modern-card-body {
  padding: 0.5rem 0.75rem;
}

.row-card .post-author {
  font-size: 0.75rem;
}

.row-card .post-store {
  font-size: 0.68rem;
}

.row-card .post-date {
  font-size: 0.63rem;
}

.row-card .post-caption {
  font-size: 0.75rem;
  margin: 0 0 0.3rem 0;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.row-card .product-info {
  margin: 0.2rem 0;
  gap: 0.15rem;
}

.row-card .product-tag {
  font-size: 0.65rem;
  padding: 0.1rem 0.45rem;
}

.row-card .comments-area {
  margin-top: 0.3rem;
}

.row-card .toggle-comments-btn {
  font-size: 0.67rem;
}

.post-caption {
  font-size: 0.8rem;
  color: #4a5568;
  margin: 0 0 0.5rem 0;
  line-height: 1.5;
}

.tombstone {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  color: #718096;
  font-size: 0.8rem;
  font-style: italic;
}

.comments-area {
  margin-top: 0.75rem;
}

.latest-comment-preview {
  display: flex;
  align-items: flex-start;
  gap: 0.25rem;
  padding: 0.4rem 0;
  margin-bottom: 0.1rem;
}

.preview-text {
  font-size: 0.76rem;
  color: #4a5568;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.preview-text strong {
  color: #2d3748;
}

.toggle-comments-btn {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.73rem;
  font-weight: 600;
  color: #718096;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  line-height: 1;
  margin-bottom: 0.1rem;
}

.toggle-comments-btn:hover { color: #4a5568; }

.product-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0.6rem 0 0.4rem;
}

.product-tag {
  display: inline-flex;
  align-items: center;
  font-size: 0.73rem;
  color: #4a5568;
  background: #edf2f7;
  border-radius: 20px;
  padding: 0.2rem 0.65rem;
  gap: 0.15rem;
  width: fit-content;
}

.product-label {
  font-weight: 600;
  color: #2d3748;
  margin-right: 0.15rem;
}

.caption-ht {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  border-radius: 20px;
  border: 1px solid;
  padding: 0.1rem 0.5rem;
  vertical-align: middle;
  line-height: 1.5;
  white-space: nowrap;
}

.caption-mention {
  color: #3b82f6;
  font-weight: 600;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// src/app/news_feed/components/NewsCard.vue